import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { api } from '@/lib/api';
import { useAuthStore, User, Tenant } from '@/stores/auth.store';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

interface LoginInput {
  email: string;
  password: string;
}

interface RegisterInput {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  organizationName: string;
}

// Backend response format (uses _id from MongoDB)
interface BackendUser {
  _id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  avatar?: string;
  phone?: string;
  tenantId: string;
  permissions: string[];
  calComLink?: string | null;
  preferences?: {
    language?: string;
    timezone?: string;
    theme?: 'light' | 'dark' | 'system';
    notifications?: { email?: boolean; push?: boolean; sms?: boolean };
  };
}

interface BackendTenant {
  _id: string;
  name: string;
  slug: string;
  logo?: string;
  industry?: string;
  businessType?: string;
  address?: {
    line1: string;
    line2?: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };
  contact?: {
    email?: string;
    phone?: string;
    website?: string;
  };
  settings?: {
    currency: string;
    timezone: string;
    dateFormat: string;
  };
  branding?: {
    primaryColor?: string;
    secondaryColor?: string;
    logoUrl?: string;
    faviconUrl?: string;
  };
}

interface AuthResponse {
  user: BackendUser;
  tenant: BackendTenant;
  tokens: {
    accessToken: string;
    refreshToken: string;
    expiresIn: string;
  };
}

// Transform MongoDB _id to frontend id
const transformUser = (user: BackendUser): User => ({
  id: user._id,
  email: user.email,
  firstName: user.firstName,
  lastName: user.lastName,
  role: user.role,
  avatar: user.avatar,
  phone: user.phone,
  tenantId: user.tenantId?.toString() || '',
  permissions: user.permissions || [],
  calComLink: user.calComLink,
  preferences: user.preferences,
});

const transformTenant = (tenant: BackendTenant): Tenant => ({
  id: tenant._id,
  name: tenant.name,
  slug: tenant.slug,
  logo: tenant.logo,
  industry: tenant.industry,
  businessType: tenant.businessType,
  address: tenant.address,
  contact: tenant.contact,
  settings: tenant.settings,
  branding: tenant.branding,
});

export function useLogin() {
  const login = useAuthStore((state) => state.login);
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (input: LoginInput) => {
      const response = await api.post<AuthResponse>('/auth/login', input);
      if (!response.success || !response.data) {
        throw new Error(response.error?.message || 'Login failed');
      }
      return response.data;
    },
    onSuccess: (data) => {
      const user = transformUser(data.user);
      const tenant = transformTenant(data.tenant);
      login(user, tenant, data.tokens.accessToken, data.tokens.refreshToken);
      toast.success('Welcome back!');
      
      // Redirect based on role
      if (user.role === 'super_admin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Invalid credentials');
    },
  });
}

export function useRegister() {
  const login = useAuthStore((state) => state.login);
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async (input: RegisterInput) => {
      const response = await api.post<AuthResponse>('/auth/register', input);
      if (!response.success || !response.data) {
        throw new Error(response.error?.message || 'Registration failed');
      }
      return response.data;
    },
    onSuccess: (data) => {
      const user = transformUser(data.user);
      const tenant = transformTenant(data.tenant);
      login(user, tenant, data.tokens.accessToken, data.tokens.refreshToken);
      toast.success('Account created successfully!');
      navigate('/dashboard');
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Registration failed');
    },
  });
}

export function useLogout() {
  const logout = useAuthStore((state) => state.logout);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async () => {
      try {
        await api.post('/auth/logout');
      } catch {
        // Ignore errors on logout
      }
    },
    onSuccess: () => {
      logout();
      queryClient.clear();
      navigate('/login');
      toast.success('Logged out successfully');
    },
  });
}

export function useCurrentUser() {
  const { isAuthenticated, setUser, setTenant, setLoading } = useAuthStore();

  const query = useQuery({
    queryKey: ['currentUser'],
    queryFn: async () => {
      const response = await api.get<{ user: BackendUser; tenant: BackendTenant }>('/auth/me');
      if (response.success && response.data) {
        const transformedUser = transformUser(response.data.user);
        const transformedTenant = transformTenant(response.data.tenant);
        setUser(transformedUser);
        setTenant(transformedTenant);
        return { user: transformedUser, tenant: transformedTenant };
      }
      throw new Error('Failed to fetch user');
    },
    enabled: isAuthenticated,
    retry: 2, // Retry a couple times in case of network issues
    retryDelay: 1000,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Don't auto-logout on errors - let the API interceptor handle 401s
  // This prevents logout on temporary network issues

  return query;
}

// Hook to refetch current user data (useful after permission updates)
export function useRefreshCurrentUser() {
  const queryClient = useQueryClient();
  const setUser = useAuthStore((state) => state.setUser);

  return useMutation({
    mutationFn: async () => {
      const response = await api.get<{ user: User; tenant: Tenant }>('/auth/me');
      if (response.success && response.data) {
        return response.data;
      }
      throw new Error('Failed to fetch user');
    },
    onSuccess: (data) => {
      setUser(data.user);
      queryClient.setQueryData(['currentUser'], data);
    },
  });
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: async (email: string) => {
      const response = await api.post('/auth/forgot-password', { email });
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to send reset email');
      }
      return response;
    },
    onSuccess: () => {
      toast.success('Password reset email sent!');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useResetPassword() {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: async ({ token, password, confirmPassword }: { token: string; password: string; confirmPassword: string }) => {
      const response = await api.post('/auth/reset-password', { token, password, confirmPassword });
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to reset password');
      }
      return response;
    },
    onSuccess: () => {
      toast.success('Password reset successfully!');
      navigate('/login');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: async ({ currentPassword, newPassword }: { currentPassword: string; newPassword: string }) => {
      const response = await api.post('/auth/change-password', { currentPassword, newPassword });
      if (!response.success) {
        throw new Error(response.error?.message || 'Failed to change password');
      }
      return response;
    },
    onSuccess: () => {
      toast.success('Password changed successfully!');
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });
}
